import { BOARD_SLUG_RENAMES } from "./renames"

export type BoardSlugRename = Readonly<{
  from: string
  to: string
  displayName?: string
}>

export type ResolvedBoardSlug = Readonly<{
  slug: string
  displayName?: string
  renameCount: number
}>

export type BoardSlugResolver = (slug: string) => ResolvedBoardSlug

const BOARD_SLUG_PATTERN = /^[a-z0-9](?:[a-z0-9-]{0,30}[a-z0-9])?$/

/** Builds a pure resolver after validating the complete rename table. */
export function createBoardSlugResolver(renames: readonly BoardSlugRename[] = BOARD_SLUG_RENAMES): BoardSlugResolver {
  if (!Array.isArray(renames)) {
    throw new TypeError("board slug renames must be an array")
  }

  const renameBySlug = new Map<string, BoardSlugRename>()
  for (const entry of renames) {
    if (
      typeof entry !== "object" ||
      entry === null ||
      !Object.hasOwn(entry, "from") ||
      !Object.hasOwn(entry, "to") ||
      typeof entry.from !== "string" ||
      typeof entry.to !== "string" ||
      !BOARD_SLUG_PATTERN.test(entry.from) ||
      !BOARD_SLUG_PATTERN.test(entry.to)
    ) {
      throw new TypeError("board slug rename has an invalid slug")
    }
    const displayName = Object.hasOwn(entry, "displayName") ? entry.displayName : undefined
    if (Object.hasOwn(entry, "displayName") && (typeof displayName !== "string" || displayName.trim() === "")) {
      throw new TypeError("board slug rename has an invalid display name")
    }
    if (renameBySlug.has(entry.from)) {
      throw new TypeError(`duplicate board slug rename: ${entry.from}`)
    }
    renameBySlug.set(
      entry.from,
      Object.freeze({
        from: entry.from,
        to: entry.to,
        ...(typeof displayName === "string" ? { displayName } : {}),
      }),
    )
  }

  for (const from of renameBySlug.keys()) {
    const visited = new Set<string>()
    let current = from
    while (renameBySlug.has(current)) {
      if (visited.has(current)) {
        throw new TypeError(`cyclic board slug rename: ${current}`)
      }
      visited.add(current)
      current = renameBySlug.get(current)?.to ?? current
    }
  }

  return (slug: string) => {
    let current = slug
    let displayName: string | undefined
    let renameCount = 0
    while (renameBySlug.has(current)) {
      const rename = renameBySlug.get(current)
      if (!rename) {
        break
      }
      current = rename.to
      renameCount += 1
      if (rename.displayName !== undefined) {
        displayName = rename.displayName
      }
    }
    return Object.freeze({
      slug: current,
      ...(displayName === undefined ? {} : { displayName }),
      renameCount,
    })
  }
}

export const resolveBoardSlug = createBoardSlugResolver()
